/* eslint-disable no-restricted-syntax */
/**
 * Handle keyboard navigation and focus events for combobox implementation
 */
export default class ComboboxNavigation {
  private el: Element | null
  private selectors: string
  private closeFn: () => void
  private focusableElements: NodeListOf<HTMLElement> | null
  private firstElement: HTMLElement | null | undefined
  private lastElement: HTMLElement | null | undefined
  private focusIndex: number | null
  private keyMap: Map<string, () => void>

  /**
   * Initializes a Combobox Navigation class
   *
   * @param el - Vue ref for parent container
   * @param selectors - Any elements to focus, as classnames. ex: ".class1,.class2"
   * @param closeFn - Function to call when escape key pressed to close menu
   */
  constructor(el: Element | null, selectors: string, closeFn: () => void) {
    this.el = el
    this.selectors = selectors
    this.closeFn = closeFn
    this.focusableElements = null
    this.firstElement = null
    this.lastElement = null
    this.focusIndex = null
    // Functions for given keys we want to handle
    this.keyMap = new Map([
      ['Escape', this.handleEscapeKey],
      ['ArrowUp', this.handleArrowUp],
      ['ArrowDown', this.handleArrowDown],
    ])
  }

  /**
   * Create array of available focusable elements, along with first & last elements
   */
  getFocusableElements(): void {
    this.focusableElements =
      this.el?.querySelectorAll<HTMLElement>(this.selectors) ?? null
    this.firstElement = this.focusableElements?.[0]
    this.lastElement =
      this.focusableElements?.[this.focusableElements?.length - 1]
  }

  /**
   * Reset temporary variables, run function to close menu
   */
  handleEscapeKey(): void {
    this.reset()
    this.closeFn()
  }

  /**
   * Run function when Down Arrow key pressed
   */
  handleArrowDown(): void {
    // on first arrow down focus on first search result
    if (this.focusIndex === null) {
      this.focusIndex = 0
      this.focusableElements?.[this.focusIndex]?.focus()
    }
    // Go back to top of list if we're at the bottom
    else if (document.activeElement === this.lastElement) {
      this.firstElement?.focus()
      this.focusIndex = 0
    }
    // Else progress through list
    else {
      this.focusIndex++
      this.focusableElements?.[this.focusIndex]?.focus()
    }
  }

  /**
   * Set focus index to null
   */
  resetIndex(): void {
    this.focusIndex = null
  }

  /**
   * Reset all temporary variables
   */
  reset(): void {
    this.resetIndex()
    this.focusableElements = null
    this.firstElement = null
    this.lastElement = null
  }

  /**
   * Run function when Up Arrow key pressed
   */
  handleArrowUp(): void {
    // Go to bottom of list if we're at the top
    if (document.activeElement === this.firstElement) {
      this.lastElement?.focus()
      this.focusIndex = this.focusableElements?.length
        ? this.focusableElements.length - 1
        : 0
    } else {
      // Else progress through list
      if (this.focusIndex !== null) {
        this.focusIndex--
      }
      this.focusableElements?.[this.focusIndex ?? 0]?.focus()
    }
  }

  /**
   * Call appropriate function for given key code
   * @param code - w3c defined key code
   */
  callKeyFn(code: string): void {
    // Create array of focusable elements
    this.getFocusableElements()
    // If we are handling current key code, run associated function. Else return
    this.keyMap.get(code)?.call(this)
  }
}
